/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/api/consolidatedFinancialsService.ts
 * PURPOSE: Consolidated Financial Reporting API Service - EPIC 10.3 Board Portal
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance - Rule 7: API Contract)
 * SPRINT GOVERNANCE: Rule 7: Performance SLAs (<50ms p95)
 *
 * CTO MANDATE: One-click Board report with <2 day generation (vs current 5-7 days)
 *
 * @ai-human-orchestration
 * - AIRole: Mid Backend Developer
 * - HumanHandoff: Local Team validates Bflow R2R integration
 * - CollaborationLevel: Remote Team implements, CFO validates report format
 *
 * @business-context
 * - Domain: Vietnamese F&B Consolidated Financial Reporting
 * - Impact: IPO Critical - Board needs monthly P&L consolidation
 * - Stakeholders: NQH CFO, Board, NQDL Subsidiary
 *
 * @technical-architecture
 * - Layer: Data Access Layer, API Services
 * - Pattern: Service Pattern, Axios Client, Type-Safe API
 * - Dependencies: Axios, TypeScript, Backend Consolidated Financials APIs
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/
 * - API: docs/03-integrate/01-API-Specifications/
 *  *
 * Sprint 10 EPIC 10.3: Consolidated Financial Reporting (4 SP)
 * - 5 BU P- 5 BU P&L consolidation (Nhat Quang Holding structure)L consolidation (Multi-BU enterprise structure)
 * - Intercompany elimination logic
 * - One-click monthly Board report
 * - <2 day generation time (vs current 5-7 days)
 */

import { apiClient } from './client';

// ============= Type Definitions =============

export interface BUFinancialData {
  bu_id: string;
  bu_name: string;
  bu_code: string;
  period: {
    start: string;
    end: string;
  };
  pnl: {
    revenue: number;
    cogs: number;
    gross_profit: number;
    gross_margin: number;
    labor_cost: number;
    operating_expenses: number;
    operating_profit: number;
    operating_margin: number;
    other_income: number;
    other_expenses: number;
    net_profit: number;
    net_margin: number;
  };
  kpis: {
    labor_cost_ratio: number;
    labor_cost_target: number;
    is_within_target: boolean;
  };
}

export interface IntercompanyElimination {
  from_entity: string;
  to_entity: string;
  description: string;
  amount: number;
  elimination_type: string;
}

export interface ConsolidatedPnL {
  revenue: number;
  cogs: number;
  gross_profit: number;
  gross_margin: number;
  labor_cost: number;
  operating_expenses: number;
  operating_profit: number;
  operating_margin: number;
  other_income: number;
  other_expenses: number;
  net_profit: number;
  net_margin: number;
}

export interface ConsolidatedFinancialsResponse {
  report_type: string;
  company: string;
  subsidiary: string;
  bus_count: number;
  consolidated: {
    period: {
      start: string;
      end: string;
    };
    consolidated_pnl: ConsolidatedPnL;
    intercompany_eliminations: {
      total: number;
      entries: IntercompanyElimination[];
    };
    kpis: {
      labor_cost_ratio: number;
      labor_cost_target: number;
      is_within_target: boolean;
    };
    bu_breakdown: BUFinancialData[];
  };
  corporate_structure: {
    holding: { id: string; name: string; type: string };
    subsidiary: { id: string; name: string; parent: string; type: string };
    bus: { id: string; name: string; code: string; parent: string; type: string }[];
  };
  export_formats: string[];
  generated_at: string;
  // DD-0067: "PENDING_REAL_DATA" while the financial feed is not wired
  data_status?: string;
  data_status_message_vi?: string;
}

export interface BURanking {
  rank: number;
  bu: string;
  value: number;
}

export interface BUComparisonResponse {
  period: {
    start: string;
    end: string;
  };
  rankings: {
    by_revenue: { rank: number; bu: string; revenue: number }[];
    by_profit_margin: { rank: number; bu: string; net_margin: number }[];
    by_labor_efficiency: { rank: number; bu: string; labor_cost_ratio: number }[];
  };
  summary: {
    best_revenue: string | null;
    best_margin: string | null;
    most_efficient: string | null;
  };
}

export interface BoardReportResponse {
  report_type: string;
  report_month: string;
  company: string;
  executive_summary: {
    total_revenue: number;
    net_profit: number;
    net_margin: number;
    labor_cost_ratio: number;
    labor_cost_status: 'within_target' | 'above_target';
    top_performer: string | null;
    most_efficient_bu: string | null;
  };
  consolidated_financials: ConsolidatedFinancialsResponse;
  bu_comparison: BUComparisonResponse;
  key_metrics: {
    roa: number;
    roe: number;
    ebitda: number;
  };
  highlights: string[];
  export_formats: string[];
  generated_at: string;
  generation_time: string;
}

// ============= Consolidated Financials API Service =============

export const consolidatedFinancialsService = {
  /**
   * Get consolidated financial report for all BUs
   * API: GET /api/v1/analytics/consolidated-financials
   *
   * @param periodStart Start date (ISO format)
   * @param periodEnd End date (ISO format)
   */
  getConsolidated: async (
    periodStart?: string,
    periodEnd?: string
  ): Promise<ConsolidatedFinancialsResponse> => {
    const params = new URLSearchParams();
    if (periodStart) params.append('period_start', periodStart);
    if (periodEnd) params.append('period_end', periodEnd);

    const response = await apiClient.get<ConsolidatedFinancialsResponse>(
      `/analytics/consolidated-financials?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get BU performance comparison for Board presentation
   * API: GET /api/v1/analytics/consolidated-financials/bu-comparison
   *
   * Returns comparative metrics for all 5 BUs including:
   * - Revenue ranking
   * - Profit margin ranking
   * - Labor cost efficiency ranking
   *
   * @param periodStart Start date
   * @param periodEnd End date
   */
  getBUComparison: async (
    periodStart?: string,
    periodEnd?: string
  ): Promise<BUComparisonResponse> => {
    const params = new URLSearchParams();
    if (periodStart) params.append('period_start', periodStart);
    if (periodEnd) params.append('period_end', periodEnd);

    const response = await apiClient.get<BUComparisonResponse>(
      `/analytics/consolidated-financials/bu-comparison?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Generate one-click monthly Board report
   * API: GET /api/v1/analytics/consolidated-financials/board-report
   *
   * Key deliverable: <2 day generation (vs current 5-7 days)
   *
   * Returns complete Board report package including:
   * - Executive summary
   * - Consolidated P&L
   * - BU breakdown
   * - Key financial metrics (ROA, ROE, EBITDA)
   * - Labor cost analysis
   * - Highlights in Vietnamese
   *
   * @param reportMonth Month in YYYY-MM format
   */
  getBoardReport: async (
    reportMonth?: string
  ): Promise<BoardReportResponse> => {
    const params = new URLSearchParams();
    if (reportMonth) params.append('report_month', reportMonth);

    const response = await apiClient.get<BoardReportResponse>(
      `/analytics/consolidated-financials/board-report?${params.toString()}`
    );
    return response.data;
  },
};

// ============= Utility Functions =============

/**
 * Format VND currency for Vietnamese display
 */
export const formatVND = (amount: number): string => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Format large VND amounts in billions (tỷ)
 */
export const formatVNDBillion = (amount: number): string => {
  const billions = amount / 1000000000;
  if (billions >= 1) {
    return `₫${billions.toFixed(1)}B`;
  }
  const millions = amount / 1000000;
  return `₫${millions.toFixed(0)}M`;
};

/**
 * Format percentage for display
 */
export const formatPercent = (value: number, decimals: number = 1): string => {
  return `${value.toFixed(decimals)}%`;
};

/**
 * Get margin status color (positive = green, negative = red)
 */
export const getMarginStatusColor = (margin: number): string => {
  if (margin >= 15) return '#22c55e'; // Green - excellent
  if (margin >= 10) return '#84cc16'; // Lime - good
  if (margin >= 5) return '#eab308'; // Yellow - acceptable
  if (margin >= 0) return '#f97316'; // Orange - low
  return '#ef4444'; // Red - negative
};

/**
 * Get labor cost status for Board display
 */
export const getLaborCostStatusDisplay = (
  status: 'within_target' | 'above_target'
): {
  color: string;
  bgColor: string;
  text: string;
  icon: string;
} => {
  if (status === 'within_target') {
    return {
      color: '#22c55e',
      bgColor: 'rgba(34, 197, 94, 0.1)',
      text: 'Đạt mục tiêu',
      icon: '✓',
    };
  }
  return {
    color: '#ef4444',
    bgColor: 'rgba(239, 68, 68, 0.1)',
    text: 'Vượt mục tiêu',
    icon: '⚠',
  };
};

/**
 * Format month for Vietnamese Board report
 */
export const formatVietnameseReportMonth = (monthStr: string): string => {
  const [year, month] = monthStr.split('-');
  const monthNames = [
    '', 'Tháng Một', 'Tháng Hai', 'Tháng Ba', 'Tháng Tư', 'Tháng Năm', 'Tháng Sáu',
    'Tháng Bảy', 'Tháng Tám', 'Tháng Chín', 'Tháng Mười', 'Tháng Mười Một', 'Tháng Mười Hai'
  ];
  return `${monthNames[parseInt(month)]} ${year}`;
};

/**
 * Get ranking badge color based on position
 */
export const getRankingBadgeColor = (rank: number): string => {
  switch (rank) {
    case 1: return '#fbbf24'; // Gold
    case 2: return '#9ca3af'; // Silver
    case 3: return '#d97706'; // Bronze
    default: return '#6b7280'; // Gray
  }
};

export default consolidatedFinancialsService;
